import React from 'react';
import { ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import I18n from 'i18n-js';

import Modal from '@reverbdotcom/commons/src/components/modal';
import ModalSidebarListing from '@reverbdotcom/commons/src/components/modal_sidebar_listing';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import AutoOfferForm from './auto_offer_form';

import { ModalSidebarListingFragment } from '@reverbdotcom/commons/src/components/modal_sidebar_listing_operations';
import { AutoOfferListingFragment } from '@reverbdotcom/commons/src/offers/offer_operations';

import { CoreAutoOfferModal } from '../../schemas/rql';

interface ExternalProps {
  listingId: string;
  modalIsOpen: boolean;
  onModalClose: () => void;
}

type Props = ChildProps<ExternalProps, CoreAutoOfferModal.Query>;

export function listingSidebarDetails({ data }: Props) {
  if (data.loading || !data.listing) return null;

  const { listing } = data;

  // The seller's price won't include VAT. If tax is present, we render an additional "list price" field.
  const shouldDisplayListPriceWithTaxIncluded = listing.taxIncluded && !!listing.taxIncludedHint;

  return [
    {
      term: I18n.t('commons.offers.sidebar.condition'),
      definition: listing.condition.displayName,
    },
    {
      term: I18n.t('commons.offers.sidebar.sellerCost'),
      definition: listing.sellerCost?.display,
    },
    {
      term: I18n.t('commons.offers.sidebar.askingPrice'),
      definition: listing.sellerPrice.display,
    },
    {
      term: I18n.t('commons.offers.sidebar.listPrice'),
      definition: shouldDisplayListPriceWithTaxIncluded && I18n.t('commons.offers.sidebar.priceWithTaxIncludedHint', {
        displayPrice: listing.price.display,
        taxIncludedHint: listing.taxIncludedHint,
      }),
    },
    {
      term: I18n.t('commons.offers.sidebar.watches'),
      definition: listing.counts.watchers,
    },
  ].filter(({ definition }) => !!definition);
}

export function AutoOfferModal(props: Props) {
  return (
    <Modal
      isOpen={props.modalIsOpen}
      paddedBody
      headerTitle={I18n.t('commons.offers.autoOffers')}
      onRequestClose={() => props.onModalClose()}
      sidebarContent={
        <ModalSidebarListing
          loading={props.data.loading}
          listing={props.data && props.data.listing}
          details={listingSidebarDetails(props)}
        />
      }
    >
      <AutoOfferForm
        loading={props.data.loading}
        listing={props.data.listing}
        closeModal={props.onModalClose}
      />
    </Modal>
  );
}

const connected = withGraphql<ExternalProps>(
  gql`
    query Core_AutoOfferModal($listingId: String) {
      listing(input: { id: $listingId }) {
        _id
        ...ModalSidebarListingFragment
        ...AutoOfferListingFragment
      }
    }
    ${ModalSidebarListingFragment}
    ${AutoOfferListingFragment}
  `,
  {
    options: {
      ssr: false, // not important for SEO or initial page rendering
    },
  },
);

export default connected(AutoOfferModal);
